import loadable from '@loadable/component';
import { useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WidgetData, WidgetEndpoint } from '../../../models/Index';
import DemoPreview from '../../demo-preview/DemoPreview';
import { useStyles } from './styles';

export const AnalogClock = loadable(() => import('analog-clock-react'));
export const DigitalClock = loadable(() => import('react-live-clock'));

type Props = {
    data: WidgetData;
    id: string;
};

function Clock(props: Props) {
    const classes = useStyles();
    const [widgetClockConfig, setWidgetClockConfig] = useState({} as WidgetEndpoint);
    const [demoWidgetClockConfig, setDemoWidgetClockConfig] = useState({} as WidgetEndpoint);
    const [hourMinuteFontSize, setHourMinuteFontSize] = useState('2rem');
    const [secondsFontSize, setSecondsFontSize] = useState('0.001rem');
    const [analogSize, setAnalogSize] = useState('125px');
    const [currentDay, setCurrentDay] = useState('MONDAY');
    /*can't default "new Date()", becasuse the timezonedDate is just a string, due to german formatting*/
    const [currentDate, setCurrentDate] = useState(null);
    const [currentDemoDate, setCurrentDemoDate] = useState(null);
    const [t] = useTranslation();
    const theme = useTheme();

    useEffect(() => {
        setWidgetClockConfig({
            timezone: props.data.data[0].timezone,
            visualization: props.data.data[0].visualization,
        } as WidgetEndpoint);

        setDemoWidgetClockConfig({
            timezone: 'Europe/London',
            visualization: props.data.data[0].visualization,
        } as WidgetEndpoint);
        const timezonedDate = new Date().toLocaleDateString('de-DE', { timeZone: props.data.data[0].timezone });
        const timezonedDateDemo = new Date().toLocaleDateString('de-DE', { timeZone: 'Europe/London' });
        const timezonedDateToIso = new Date(convertToISODate(timezonedDate));
        setCurrentDay(getDayName(timezonedDateToIso.getDay()));
        setCurrentDate(timezonedDate);
        setCurrentDemoDate(timezonedDateDemo);
        // eslint-disable-next-line
    }, [props.data]);

    useEffect(() => {
        const height = document?.getElementById(`widget-content-height-${props.data.key}`)?.offsetHeight;
        const width = document?.getElementById(`widget-content-height-${props.data.key}`)?.offsetWidth;
        if (height > 0) {
            /*date*/
            setSecondsFontSize(
                height < 20
                    ? '1rem'
                    : height < 100
                    ? '2rem'
                    : height < 150
                    ? '2.5rem'
                    : height < 200
                    ? '3rem'
                    : width < 150
                    ? '2rem'
                    : '3.5rem',
            );
            /*clock*/
            setHourMinuteFontSize(
                height < 20 ? '' : height < 80 ? '' : height < 150 ? '' : height < 200 ? '6rem' : width < 150 ? '6rem' : '7rem',
            );
            setAnalogSize(height < 125 ? '75px' : '125px');
        }
        // eslint-disable-next-line
    }, [
        document?.getElementById(`widget-content-height-${props.data.key}`)?.offsetHeight,
        document?.getElementById(`widget-content-height-${props.data.key}`)?.offsetWidth,
    ]);

    useEffect(() => {}, [widgetClockConfig]);

    const convertToISODate = (dateString) => {
        const parts = dateString.split('.');
        const day = parts[0].padStart(2, '0');
        const month = parts[1].padStart(2, '0');
        const year = parts[2];

        return `${year}-${month}-${day}`;
    };

    function getDayName(day: number) {
        return {
            0: 'SUNDAY',
            1: 'MONDAY',
            2: 'TUESDAY',
            3: 'WEDNESDAY',
            4: 'THURSDAY',
            5: 'FRIDAY',
            6: 'SATURDAY',
        }[day];
    }

    return (
        <div className={classes.root} id="widget-content-height" style={{ color: props.data.fontColor }}>
            {props.data.isUsingDefault ? (
                <DemoPreview
                    isClock={true}
                    secondsFontSize={secondsFontSize}
                    currentDay={currentDay}
                    currentDemoDate={currentDemoDate}
                    hourMinuteFontSize={hourMinuteFontSize}
                    demoWidgetClockConfig={demoWidgetClockConfig}
                />
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {/*date*/}
                    <div className={classes.date} style={{ fontSize: `${secondsFontSize}` }}>
                        <div style={{ paddingRight: '4px' }}>{t(`COMMON.TIME_${currentDay}_TEXT`)}</div>
                        <div style={{ paddingLeft: '4px' }}>{currentDate}</div>
                    </div>
                    {/*clock*/}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            fontSize: `${hourMinuteFontSize}`,
                            fontWeight: theme.typography.fontWeightBold,
                        }}
                    >
                        {widgetClockConfig.visualization === 'analog' ? (
                            <AnalogClock
                                {...{
                                    width: analogSize,
                                    border: false,
                                    borderColor: 'white',
                                    baseColor: 'white',
                                    centerColor: 'black',
                                    centerBorderColor: 'white',
                                    handColors: {
                                        second: 'black',
                                        minute: 'black',
                                        hour: 'black',
                                    },
                                }}
                            />
                        ) : (
                            <DigitalClock format={'HH:mm'} ticking={true} timezone={widgetClockConfig.timezone} />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default Clock;
